import { Injectable } from '@angular/core';
import {
  CreateOrUpdateRequest,
  ZraProductDetail,
  ZraProductDetailService,
} from '@boma/supplier-portal/shared/services';
import { Store } from '@ngrx/store';
import { getSelectedOrganizationInfo } from '../shared-state';
import {
  catchError,
  finalize,
  map,
  Observable,
  of,
  switchMap,
  tap,
  throwError,
} from 'rxjs';

import { BehaviorSubject } from 'rxjs';

export interface ZraError {
  message: string;
  status?: number;
  timestamp?: string;
}

export interface ZraProductDetailState {
  productDetails: Map<string, ZraProductDetail>;
  loading: boolean;
  createOrUpdateLoading: boolean;
  error: ZraError | null;
}

@Injectable()
export class ZraProductDetailFacade {
  private readonly _entityId$: Observable<string>;
  private readonly _state$: BehaviorSubject<ZraProductDetailState> =
    new BehaviorSubject<ZraProductDetailState>({
      productDetails: new Map(),
      loading: false,
      createOrUpdateLoading: false,
      error: null,
    });

  getProductDetail$(
    productId: string
  ): Observable<ZraProductDetail | undefined> {
    return this._state$.pipe(
      map((state) => state.productDetails.get(productId))
    );
  }

  productDetails$: Observable<ZraProductDetail[]> = this._state$.pipe(
    map((state) => Array.from(state.productDetails.values()))
  );

  loading$: Observable<boolean> = this._state$.pipe(
    map((state) => state.loading)
  );
  creatingOrUpdating$: Observable<boolean> = this._state$.pipe(
    map((state) => state.createOrUpdateLoading)
  );
  error$: Observable<ZraError | null> = this._state$.pipe(
    map((state) => state.error)
  );

  constructor(
    private store: Store,
    private zraProductDetailService: ZraProductDetailService
  ) {
    this._entityId$ = this.store
      .select(getSelectedOrganizationInfo)
      .pipe(map((info) => info.id));
  }

  getProductDetails(productId: string) {
    this._updateState({ loading: true, error: null });

    return this._entityId$
      .pipe(
        switchMap((entityId) =>
          this.zraProductDetailService
            .getProductDetails(entityId, productId)
            .pipe(
              catchError((error) => {
                this._handleError('Failed to get product details', error);
                return of(null);
              })
            )
        ),
        finalize(() => this._updateState({ loading: false }))
      )
      .subscribe({
        next: (productDetail) => {
          if (productDetail) {
            this._updateProductDetails(productDetail);
          }
        },
      });
  }

  createOrUpdate(request: CreateOrUpdateRequest): Observable<ZraProductDetail | null> {
    this._updateState({ createOrUpdateLoading: true, error: null });

    return this._entityId$.pipe(
      switchMap((entityId) =>
        this.zraProductDetailService.createOrUpdate(entityId, request).pipe(
          tap((productDetail) => {
            if (productDetail) {
              this._updateProductDetails(productDetail);
            }
          }),
          catchError((error) => {
            console.log('error in pipe', error);
            this._handleError('Failed to create or update product', error);
            this._updateState({ createOrUpdateLoading: false });
            return of(null);
          }),
          finalize(() => this._updateState({ createOrUpdateLoading: false }))
        )
      )
    );
  }

  syncWithZra(productId: string): Observable<any> {
    this._updateState({ createOrUpdateLoading: true, error: null });

    return this._entityId$.pipe(
      switchMap((entityId) =>
        this.zraProductDetailService.syncWithZra(entityId, productId).pipe(
          tap((syncResponse) => {
            if (syncResponse) {
              // After sync, refresh the product details
              this.getProductDetails(productId);
            }
          }),
          catchError((error) => {
            this._handleError('Failed to sync with ZRA', error);
            this._updateState({ createOrUpdateLoading: false });
            return throwError(() => error);
          }),
          finalize(() => this._updateState({ createOrUpdateLoading: false }))
        )
      )
    );
  }

  _updateProductDetails(productDetail: ZraProductDetail): void {
    const updatedMap = new Map(this._state$.value.productDetails);
    updatedMap.set(productDetail.productId, productDetail);
    this._updateState({
      productDetails: updatedMap,
      loading: false,
      createOrUpdateLoading: false
    });
  }

  _updateState(state: Partial<ZraProductDetailState>): void {
    this._state$.next({ ...this._state$.value, ...state });
  }

  private _handleError(context: string, error: any): void {
    console.error(`${context}:`, error);

    const errorObj: ZraError = {
      message: error.message || context,
      status: error.status,
      timestamp: new Date().toISOString(),
    };

    this._updateState({ error: errorObj, loading: false });
  }
}
